import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";

const COACH_IMG =
  "https://images.unsplash.com/photo-1573164574048-f968d7ee9f20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMG1lbnRvcmluZyUyMGRpdmVyc2UlMjBvZmZpY2V8ZW58MHx8fHwxNzg3MDc4NTcyfDA&ixlib=rb-4.1.0&q=85";

const steps = [
  { num: "01", title: "Map your credentials", desc: "We assess your education and experience against US market expectations — and identify exactly what transfers." },
  { num: "02", title: "Choose the right course", desc: "CPA, CMA, EA, QuickBooks, Excel, or a degree bridge — invest only in what moves you toward the role you want." },
  { num: "03", title: "Position your story", desc: "Resume, LinkedIn, and interview narrative rebuilt for US hiring managers who don't know your home market." },
  { num: "04", title: "Land the role", desc: "Targeted applications, interview preparation, and negotiation guidance — from arrival to offer letter." },
];

export default function Coaching() {
  return (
    <section id="coaching" data-testid="coaching-section" className="bg-pine text-bone">
      <div className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40">
        <div className="grid grid-cols-1 gap-16 md:grid-cols-12">
          <div className="md:col-span-6">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="mb-6 font-mono text-[11px] uppercase tracking-[0.35em] text-terra"
            >
              Career Coaching · For Immigrant Professionals
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
              className="mb-8 font-serif text-4xl font-medium leading-tight tracking-tight md:text-5xl"
              data-testid="coaching-heading"
            >
              New to the US market? <span className="italic text-terra">I've walked that road.</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="mb-10 max-w-lg text-base leading-relaxed text-bone/70"
              data-testid="coaching-copy"
            >
              I arrived in the US with an international degree and built a career that led to
              the CFO chair. Now I help new immigrant professionals navigate which courses and
              credentials actually matter — and how to turn international experience into US offers.
            </motion.p>
            <motion.div
              initial={{ clipPath: "inset(0 100% 0 0)" }}
              whileInView={{ clipPath: "inset(0 0% 0 0)" }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1] }}
              className="aspect-[16/10] overflow-hidden border border-bone/10"
            >
              <img
                src={COACH_IMG}
                alt="Mentoring session between two professionals"
                data-testid="coaching-image"
                className="h-full w-full object-cover"
              />
            </motion.div>
          </div>
          <div className="md:col-span-6">
            <div className="flex flex-col">
              {steps.map((s, i) => (
                <motion.div
                  key={s.num}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.8, delay: i * 0.08, ease: [0.76, 0, 0.24, 1] }}
                  className="border-b border-bone/10 py-10"
                  data-testid={`coaching-step-${s.num}`}
                >
                  <div className="flex items-baseline gap-6">
                    <span className="font-mono text-sm text-terra">{s.num}</span>
                    <div>
                      <h3 className="mb-3 font-serif text-2xl font-semibold tracking-tight">{s.title}</h3>
                      <p className="max-w-md text-sm leading-relaxed text-bone/60">{s.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <motion.a
              href="#contact"
              data-testid="coaching-cta-btn"
              whileTap={{ scale: 0.96 }}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-12 inline-flex items-center gap-2 border border-terra bg-terra px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-transparent hover:text-terra"
            >
              Start Your Roadmap <ArrowDownRight size={16} />
            </motion.a>
          </div>
        </div>
      </div>
    </section>
  );
}
